import { Layout } from '@/components/layouts/layout';
import { useCreateAuction } from '@/hooks/use-auctions';
import { AUCTION_PLATFORMS } from '@/types/auctions';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/auctions/new')({
    component: NewAuctionPage,
});

function NewAuctionPage() {
    const navigate = useNavigate();
    const createMutation = useCreateAuction();

    const [title, setTitle] = useState('');
    const [platform, setPlatform] = useState('Whatnot');
    const [scheduledAt, setScheduledAt] = useState('');
    const [notes, setNotes] = useState('');

    async function handleCreate() {
        if (!title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        createMutation.mutate(
            {
                title: title.trim(),
                platform,
                scheduled_at: scheduledAt || null,
                notes: notes.trim() || null,
            },
            {
                onSuccess: (event) => {
                    toast.success('Auction created');
                    navigate({
                        to: '/auctions/$auctionId',
                        params: { auctionId: event.id },
                    });
                },
                onError: () => toast.error('Failed to create auction'),
            },
        );
    }

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="max-w-170">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <h1 className="text-[30px] font-bold tracking-tight mb-1.5 [font-family:var(--font-heading)] text-[#f5f7fb]">
                                New Auction
                            </h1>
                            <p className="text-[#9aa2c0] text-[14.5px]">
                                Set up an event, then build its queue.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate({ to: '/auctions' })}
                            className="text-[13.5px] font-semibold px-3.75 py-2.5 rounded-[9px] cursor-pointer text-[#cdd3e6] bg-[#161d2e] border border-[#2a3450] hover:bg-[#1e2840]"
                        >
                            Cancel
                        </button>
                    </div>

                    {/* Form card */}
                    <div className="rounded-[18px] p-6 bg-[#111726] border border-[#232b3f]">
                        {/* Title */}
                        <label className="block text-[12.5px] font-semibold mb-2 text-[#9aa2c0]">
                            Title
                        </label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Whatnot Show — June 11"
                            className="w-full rounded-[11px] px-3.75 py-3.25 text-[15px] outline-none mb-5.5 bg-[#0c111d] border border-[#2a3450] text-[#f5f7fb]"
                        />

                        {/* Platform */}
                        <label className="block text-[12.5px] font-semibold mb-2.5 text-[#9aa2c0]">
                            Platform
                        </label>
                        <div className="flex flex-wrap gap-2.25 mb-5.5">
                            {AUCTION_PLATFORMS.map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setPlatform(p)}
                                    className={`flex items-center gap-2 text-[13.5px] font-semibold px-3.75 py-2.5 rounded-[10px] cursor-pointer transition-colors border ${platform === p ? 'text-white bg-primary-500 border-primary-500' : 'text-[#aeb6d0] bg-[#0f1626] border-[#242c40]'}`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>

                        {/* Schedule */}
                        <label className="block text-[12.5px] font-semibold mb-2 text-[#9aa2c0]">
                            Scheduled{' '}
                            <span className="text-[#5d667e] font-normal">
                                · optional
                            </span>
                        </label>
                        <input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            className="w-full rounded-[11px] px-3.75 py-3 text-[14px] outline-none mb-5.5 bg-[#0c111d] border border-[#2a3450] text-[#f5f7fb]"
                            style={{ colorScheme: 'dark' }}
                        />

                        {/* Notes */}
                        <label className="block text-[12.5px] font-semibold mb-2 text-[#9aa2c0]">
                            Notes{' '}
                            <span className="text-[#5d667e] font-normal">
                                · optional
                            </span>
                        </label>
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Lot themes, pricing strategy, reminders..."
                            className="w-full rounded-[11px] px-3.75 py-3.25 text-[14.5px] outline-none resize-y leading-relaxed bg-[#0c111d] border border-[#2a3450] text-[#f5f7fb] font-[inherit]"
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-2.75 mt-4.5">
                        <button
                            onClick={() => navigate({ to: '/auctions' })}
                            className="text-[14px] font-semibold px-4.5 py-3 cursor-pointer text-[#9aa2c0]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreate}
                            disabled={createMutation.isPending}
                            className="flex items-center gap-2 font-semibold text-[14px] text-white px-5.5 py-3 rounded-[10px] cursor-pointer disabled:opacity-60 transition-colors bg-primary-500 hover:bg-primary-700"
                        >
                            {createMutation.isPending
                                ? 'Creating…'
                                : 'Create Auction'}
                            <ArrowRight size={17} color="#fff" />
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
